import { toEDNString, toEDNStringFromSimpleObject } from "edn-data";
import { queryOmegaSingleResult } from "../api";
import { Chart, ChartSourceData } from "../types/chart";

export const createChart: (chart: Chart) => Promise<any> = async (chart) => {
  const ednString = toEDNStringFromSimpleObject(chart as any, {
    keysAs: "string",
  });
  const query = `
    (datastore Qo.Public.Api.Chart "omega/query-omega/public/api/chart")
    (Qo.Public.Api.Chart/create-chart ${ednString} Response)
    (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getPageCharts: (config: {
  appId: string;
  pageId: string;
}) => Promise<Chart[]> = async ({ appId, pageId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["page-id", pageId],
    ],
  });
  const query = `
      (datastore Qo.Public.Api.Chart "omega/query-omega/public/api/chart")
      (Qo.Public.Api.Chart/get-page-charts ${ednString} Response)
      (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getChart: (config: {
  "chart-id": string;
}) => Promise<Chart[]> = async (config) => {
  const ednString = toEDNStringFromSimpleObject(config, { keysAs: "string" });
  const query = `
      (datastore Qo.Public.Api.Chart "omega/query-omega/public/api/chart")
      (Qo.Public.Api.Chart/get-chart ${ednString} Response)
      (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const setChart: (config: {
  appId: string;
  chartId: string;
  chartData: Chart;
}) => Promise<void> = async ({ appId, chartId, chartData }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["chart-id", chartId],
      [
        "data",
        {
          map: [
            ["name", chartData.name],
            ["code", chartData.code],
            ["inputs", chartData.inputs ?? []],
          ],
        },
      ],
    ],
  });
  const query = `
      (datastore Qo.Public.Api.Chart "omega/query-omega/public/api/chart")
      (Qo.Public.Api.Chart/update-chart ${ednString} Response)
      (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const deleteChart: (config: {
  appId: string;
  chartId: string;
  pageId: string;
}) => Promise<void> = async ({ appId, chartId, pageId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["chart-id", chartId],
      ["page-id", pageId],
    ],
  });
  const query = `
      (datastore Qo.Public.Api.Chart "omega/query-omega/public/api/chart")
      (Qo.Public.Api.Chart/delete-chart ${ednString} Response)
      (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

/**
 * The chart's Chart Record by block ids, through the polymorphic block surface
 * (Qo.Api.Bl/get-data-by-id dispatches on the block type to Qo.Page.Bl.Chart/get-object) —
 * the same call the volume block uses (src/api/volume.ts).
 */
export const getChartById: (config: {
  "app-id": string;
  "page-id": string;
  "block-id": string;
}) => Promise<Chart> = async (config) => {
  const appId = JSON.stringify(config["app-id"]);
  const pageId = JSON.stringify(config["page-id"]);
  const blockId = JSON.stringify(config["block-id"]);
  const query = `
        (datastore Qo.Api.Bl "omega/query-omega/api/block")
        (Qo.Api.Bl/get-data-by-id ${appId} ${pageId} ${blockId} Response)
        (return [Response])`;
  return queryOmegaSingleResult(query);
};

/**
 * Run the chart's Chart Source (query-omega-api/oql/app/api/chart.oql,
 * Qo.Api.Chart/source-data-by-id): the source Query page's run answer.
 */
export const getChartSourceData: (config: {
  "app-id": string;
  "page-id": string;
  "block-id": string;
  inputValues?: Record<string, unknown>;
}) => Promise<ChartSourceData> = async (config) => {
  const appId = JSON.stringify(config["app-id"]);
  const pageId = JSON.stringify(config["page-id"]);
  const blockId = JSON.stringify(config["block-id"]);
  // Input Values (query-omega-oql/pages/oql/terms.html): merged over source.input by the engine, never stored.
  const inputValues = toEDNStringFromSimpleObject((config.inputValues ?? {}) as any, { keysAs: "string" });
  const query = `
        (datastore Qo.Api.Chart "omega/query-omega/api/chart")
        (Qo.Api.Chart/source-data-by-id-with-input ${appId} ${pageId} ${blockId} ${inputValues} Response)
        (return [Response])`;
  return queryOmegaSingleResult(query);
};

/** The choices of one input's x-options Query (Qo.Api.Chart/input-options-by-id): column 0 = value, 1 = label. */
export const getChartInputOptions: (config: {
  "app-id": string;
  "page-id": string;
  "block-id": string;
  name: string;
}) => Promise<ChartSourceData> = async (config) => {
  const appId = JSON.stringify(config["app-id"]);
  const pageId = JSON.stringify(config["page-id"]);
  const blockId = JSON.stringify(config["block-id"]);
  const name = JSON.stringify(config.name);
  const query = `
        (datastore Qo.Api.Chart "omega/query-omega/api/chart")
        (Qo.Api.Chart/input-options-by-id ${appId} ${pageId} ${blockId} ${name} Response)
        (return [Response])`;
  return queryOmegaSingleResult(query);
};
